<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router/composables'
// @ts-expect-error js file
import store from '@/store'
import { Studio, StudioTabsInfo } from '@/modules/Studios/types'
import { StudioApi } from '@/enitites/studio'
import StudioHeader from '@/modules/Studios/components/StudioHeader.vue'
import StudioTabs from '@/modules/Studios/components/StudioTabs.vue'
import StudioCompetitionsTab from '@/modules/Studios/components/tabs/StudioCompetitionsTab.vue'
import StudioStudentsTab from '@/modules/Studios/components/tabs/StudioStudentsTab.vue'
import StudioTeachersTab from '@/modules/Studios/components/tabs/StudioTeachersTab.vue'
import StudioAboutTab from '@/modules/Studios/components/tabs/StudioAboutTab.vue'
import Bugsnag from '@bugsnag/js'

const route = useRoute()

const studio = ref<Studio>()
const studioDetails = ref<Studio>()
const tab = ref(0)
const loading = ref(false)

const tabs = ref<StudioTabsInfo[]>([
  { id: 0, name: 'students' },
  { id: 1, name: 'teachers' },
  { id: 2, name: 'competitions' },
  { id: 3, name: 'about' },
])

const initStudio = async () => {
  loading.value = true
  try {
    const studioId = Number(route.params.id)
    const studioData = await StudioApi.fetchStudio(studioId)

    if (!studioData) {
      await store.dispatch('setIsNotFound', true)
      loading.value = false
      return
    }

    studio.value = studioData
    studioDetails.value = studioData
  } catch (error) {
    Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), (event) => {
      event.addMetadata('studio', {
        context: 'Error fetching studio',
        studioId: route.params.id,
      })
    })
    await store.dispatch('setIsNotFound', true)
  } finally {
    loading.value = false
  }
}

const initTab = () => {
  if (!route.query.tab) return
  const currentTabItem = tabs.value.find((tabItem) => tabItem.name === route.query.tab)
  if (currentTabItem) tab.value = currentTabItem.id
}

onMounted(async () => {
  await initStudio()
  initTab()
})

// Watch for route query changes to update the tab
watch(
  () => route.query.tab,
  (newTab) => {
    if (newTab) {
      const currentTabItem = tabs.value.find((tabItem) => tabItem.name === newTab)
      if (currentTabItem) tab.value = currentTabItem.id
    } else {
      // Default to the first tab if no tab is specified in the route
      tab.value = 0
    }
  }
)
</script>

<template>
  <section class="studio">
    <div
      v-if="studio"
      class="studio-container"
    >
      <StudioHeader :studio="studio" />

      <StudioTabs
        :tabs="tabs"
        :current-tab="tab"
        @update:current-tab="tab = $event"
      />

      <div
        v-if="!loading"
        class="tab-items"
      >
        <v-tabs-items v-model="tab">
          <!-- Students Tab (id: 0) -->
          <v-tab-item class="item">
            <StudioStudentsTab :studio="studio" />
          </v-tab-item>

          <!-- Teachers Tab (id: 1) -->
          <v-tab-item class="item">
            <StudioTeachersTab :studio="studio" />
          </v-tab-item>

          <!-- Competitions Tab (id: 2) -->
          <v-tab-item class="item">
            <StudioCompetitionsTab :studio-details="studioDetails" />
          </v-tab-item>

          <!-- About Tab (id: 3) -->
          <v-tab-item class="item">
            <StudioAboutTab
              :studio="studio"
              :studio-details="studioDetails"
            />
          </v-tab-item>
        </v-tabs-items>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.studio {
  padding-top: var(--header-height);
  background-color: var(--color-grey-100);
}

.studio-container {
  width: 100%;
  max-width: 880px;
  margin: 40px auto 0;

  @media (max-width: $screen-md) {
    margin-top: 20px;
    padding: 0 20px;
  }
}

.tab-items {
  .item {
    min-height: 500px;
    padding-top: 40px;
    background-color: var(--color-grey-100);

    @media (max-width: $screen-md) {
      padding-top: 20px;
    }
  }
}
</style>
